import React, { useEffect, useMemo, useRef, useState } from 'react';
import { Image } from "@/components/common/image";
import { Video } from "@/components/common/video";
import { cn } from '@/lib/utils';
import { IconButton } from '@/components/common/icon-button';
import { ChevronDown, ChevronUp, X } from 'lucide-react';
import { DeleteConfirmModal, Spinner } from '../ui/modal';
import VideoController from '../video-controller';
import * as Dialog from "@radix-ui/react-dialog";
import { MoveIcon } from '../assets-material-item/assets';
import { TrashIcon } from '../ui/icon';
import dayjs from 'dayjs';

interface MaterialPreviewVideoImageProps {
  /**
   * 预览列表
   * 传递多张图片/视频URL时，开启多图/视频查看
   */
  previewList: {
    id: number,
    url: string,
    videoCoverUrl?: string,
    type: 'image' | 'video',
    tags?: string[];
    createTime?: number;
  }[];
  alt?: string;
  /** 当前预览项索引 */
  currentIndex?: number;
  open?: boolean;
  deleteLoading?: boolean;
  onOpenChange?: (open: boolean) => void;
  onDelete?: (id: number) => void;
  onMove?: (id: number) => void;
}

/** 视频图片预览组件 */
export const MaterialPreviewVideoImageFull: React.FC<MaterialPreviewVideoImageProps> = ({
  open = false,
  previewList,
  currentIndex = 0,
  alt = '查看媒体',
  deleteLoading = false,
  onOpenChange,
  onDelete,
  onMove,
}) => {

  const maskRef = useRef<HTMLDivElement>(null);
  const [index, setIndex] = useState(currentIndex);

  const videoRef = useRef<HTMLVideoElement>(null);
  const [load, setLoad] = useState(false);

  // 是否开启多图/视频查看
  const multiple = useMemo(() => {
    return Array.isArray(previewList) && previewList.length > 1;
  }, [previewList]);

  // 获取当前预览项
  const currentItem = useMemo(() => {
    if (Array.isArray(previewList) && previewList.length > 0) {
      return previewList[index] || previewList[0];
    }
    return null;
  }, [previewList, index]);


  // 格式化创建时间（超过一年展示完整时间，否则展示月日小时分钟）
  const formattedCreateTime = useMemo(() => {
    if (!currentItem?.createTime) return '';

    const createDate = dayjs(currentItem.createTime);
    const now = dayjs();
    const yearsDiff = now.diff(createDate, 'year');

    return createDate.format(yearsDiff > 0 ? 'YYYY-MM-DD HH:mm' : 'MM-DD HH:mm');
  }, [currentItem?.createTime]);

  // 切换上一张图片/视频
  const handlePrev = () => {
    if (multiple && Array.isArray(previewList)) {
      setIndex((prevIndex) => (prevIndex - 1 + previewList.length) % previewList.length);
    }
  };

  // 切换下一张图片/视频
  const handleNext = () => {
    if (multiple && Array.isArray(previewList)) {
      setIndex((prevIndex) => (prevIndex + 1) % previewList.length);
    }
  };

  // 点击蒙层关闭
  const handleMaskClick = (e: React.MouseEvent<HTMLDivElement>) => {
    if (e.target === maskRef.current) {
      onOpenChange?.(false);
    }
  };

  // 监听 currentIndex 变化，更新当前预览项索引
  useEffect(() => {
    if (currentIndex !== null && multiple && Array.isArray(previewList)) {
      // 确保索引在有效范围内
      setIndex(Math.max(0, Math.min(currentIndex, previewList.length - 1)));
    }
  }, [currentIndex, previewList, multiple]);

  // 自动播放视频
  useEffect(() => {
    const video = videoRef.current;
    if (!video || currentItem?.type !== 'video') return;

    const handleCanPlayThrough = async () => {
      try {
        await video.play();
      } catch (error) {
        console.warn("Auto-play failed:", error);
      }
    };

    video.addEventListener("canplaythrough", handleCanPlayThrough);

    // 如果视频已经可以播放，立即尝试播放
    if (video.readyState >= 3) {
      handleCanPlayThrough();
    }

    return () => {
      video.removeEventListener("canplaythrough", handleCanPlayThrough);
    };
  }, [currentItem, videoRef.current]);

  // 键盘导航
  useEffect(() => {
    const handleKeyDown = (e: KeyboardEvent) => {
      if (!open) return;

      switch (e.key) {
        case "ArrowUp":
          handlePrev();
          break;
        case "ArrowDown":
          handleNext();
          break;
        case "Escape":
          onOpenChange?.(false);
          break;
      }
    };

    document.addEventListener("keydown", handleKeyDown);
    return () => document.removeEventListener("keydown", handleKeyDown);
  }, [open, onOpenChange]);

  if (!previewList || previewList.length === 0 || !currentItem) return null;

  return (
    <Dialog.Root open={open} onOpenChange={onOpenChange}>
      <Dialog.Portal>
        <Dialog.Overlay className={cn("fixed inset-0 bg-black/50 z-50")} />
        <Dialog.Content>
          <div
            ref={maskRef}
            className={cn(
              "fixed inset-0 bg-black/80 z-[999] flex items-center justify-center p-4 select-none",
              multiple && "p-0 bg-black"
            )}
            onClick={handleMaskClick}
          >
            {/* 根据当前项类型显示图片或视频 */}
            {currentItem.type === 'image' ? (
              <Image
                src={currentItem.url}
                alt={alt}
                className="max-w-full max-h-full object-contain"
                onDragStart={(e) => e.preventDefault()}
                loading="lazy"
              />
            ) : (
              <>
                <div
                  className="flex-1 flex items-center justify-center"
                  onClick={(e) => e.stopPropagation()}
                >
                  <div className="absolute top-0 bottom-0 aspect-[9/16]">
                    <Video
                      ref={videoRef}
                      src={currentItem.url}
                      poster={currentItem?.videoCoverUrl || ''}
                      className="h-full object-contain"
                      autoPlay
                      preload="none"
                      onClick={() => videoRef.current?.paused ? videoRef.current?.play() : videoRef.current?.pause()}
                    />
                    {/* 视频控制器 */}
                    <div className="absolute bottom-0 left-0 right-0 transition-opacity duration-250 z-[1000]" onClick={(e) => e.stopPropagation()}>
                      <VideoController videoRef={videoRef} onLoad={setLoad} />
                    </div>
                  </div>
                </div>
                {load && (
                  <div className="absolute inset-0 flex justify-center items-center bg-black/20">
                    <Spinner className="text-white" size={32} />
                  </div>
                )}
              </>
            )}

            {/* 关闭 */}
            <IconButton
              icon={<X className="size-5 text-gray-500" />}
              className='size-10 absolute top-8 right-8 bg-white hover:bg-white/80 border border-[rgba(208,213,221,1)] rounded-[8px]'
              onClick={() => onOpenChange?.(false)}
            />

            {/* 只有多项时显示上下切换按钮 */}
            {multiple && (
              <div className="absolute top-1/2 right-8 transform -translate-y-1/2 space-y-6">
                <IconButton
                  icon={<ChevronUp className="size-6 text-white" />}
                  className='size-10 bg-white/30 hover:bg-[rgba(255,255,255,0.2)] rounded-[10px]'
                  onClick={handlePrev}
                />
                <IconButton
                  icon={<ChevronDown className="size-6 text-white" />}
                  className='size-10 bg-white/30 hover:bg-[rgba(255,255,255,0.2)] rounded-[10px]'
                  onClick={handleNext}
                />
              </div>
            )}
            {/* 素材标签、移动、删除 */}
            <div className="fixed bottom-8 right-8 z-[1001] max-w-[200px] pointer-events-none">
              <div className="flex flex-col items-end gap-3 pointer-events-auto">
                {/* 标签 */}
                <TagList key={currentItem.id} tags={currentItem.tags} maxRows={2} />
                <p className="text-white text-[13px]">
                  {formattedCreateTime}
                </p>
                <div className="flex items-center gap-5">
                  <IconButton
                    className="hover:bg-white/30"
                    icon={<MoveIcon className="text-white size-5" />}
                    onClick={() => onMove?.(currentItem.id)}
                  />
                  <DeleteConfirmModal
                    trigger={
                      <IconButton
                        className="hover:bg-white/30"
                        icon={<TrashIcon className="text-white size-5" />}
                      />
                    }
                    onConfirm={() => onDelete?.(currentItem.id)}
                    loading={deleteLoading}
                  />
                </div>
              </div>
            </div>
          </div>
        </Dialog.Content>
      </Dialog.Portal>
    </Dialog.Root>
  )
};

interface TagListProps {
  tags?: string[];
  maxRows?: number;
}
// 标签列表
const TagList: React.FC<TagListProps> = ({ tags = [], maxRows = 2 }) => {
  const containerRef = useRef<HTMLDivElement>(null);
  const [visibleCount, setVisibleCount] = useState(tags.length);

  useEffect(() => {
    const el = containerRef.current;
    if (!el) return;

    const calc = () => {
      const children = Array.from(el.children) as HTMLElement[];
      const rows = new Map<number, HTMLElement[]>();

      children.forEach((child) => {
        const top = child.offsetTop;
        if (!rows.has(top)) rows.set(top, []);
        rows.get(top)!.push(child);
      });

      if (rows.size <= maxRows) {
        setVisibleCount(children.length);
        return;
      }

      // 只保留前 maxRows 行
      const allowedTops = Array.from(rows.keys())
        .sort((a, b) => a - b)
        .slice(0, maxRows);

      let count = 0;
      children.forEach((child) => {
        if (allowedTops.includes(child.offsetTop)) {
          count++;
        }
      });

      setVisibleCount(count);
    };

    calc();

    const ro = new ResizeObserver(calc);
    ro.observe(el);

    return () => ro.disconnect();
  }, [tags, maxRows]);

  const hiddenCount = tags.length - visibleCount;
  const visibleTags = hiddenCount > 0 ? tags.slice(0, visibleCount - 1) : tags.slice(0, visibleCount);

  return (
    <div
      ref={containerRef}
      className="w-full flex flex-wrap gap-1.5 justify-end overflow-hidden"
    >
      {visibleTags.map((tag, i) => (
        <span
          key={i}
          className="px-2 py-1.5 rounded-[99px] bg-white/30 text-xs text-white font-medium break-all"
        >
          {tag}
        </span>
      ))}

      {hiddenCount > 0 && (
        <span className="px-2 py-1.5 rounded-[99px] bg-white/30 text-xs text-white font-medium whitespace-nowrap">
          +{hiddenCount}
        </span>
      )}
    </div>
  );
};
